import { get } from 'lodash-es';
import { Sort, SortDirection } from '@inst-iot/bosch-angular-ui-components';

type NormalizedSortValue = {
  isNull: boolean;
  num: number | null;
  str: string;
};

function isMissingSortValue(value: any): boolean {
  return value === null || value === undefined;
}

function isMissingSortString(value: string): boolean {
  const normalizedValue = value.toLowerCase();
  return value === '' || normalizedValue === 'n/a';
}

function parseSortNumber(value: string): number | null {
  const parsed = parseFloat(value.replace(/%/g, '').replace(/,/g, ''));
  return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
}

function normalizeSortValue(value: any): NormalizedSortValue {
  if (isMissingSortValue(value)) {
    return { isNull: true, num: null, str: '' };
  }

  if (typeof value === 'number') {
    return { isNull: false, num: value, str: '' };
  }

  const stringValue = String(value).trim();
  if (isMissingSortString(stringValue)) {
    return { isNull: true, num: null, str: '' };
  }

  const parsed = parseSortNumber(stringValue);
  if (parsed !== null) {
    return { isNull: false, num: parsed, str: '' };
  }

  return { isNull: false, num: null, str: stringValue.toLowerCase() };
}

function compareNullValues(first: NormalizedSortValue, second: NormalizedSortValue): number | null {
  if (!first.isNull && !second.isNull) return null;
  if (first.isNull && second.isNull) return 0;
  return first.isNull ? 1 : -1;
}

function compareNumericValues(first: number | null, second: number | null, directionFactor: number): number | null {
  if (first === null && second === null) return null;
  if (first === null) return directionFactor;
  if (second === null) return -directionFactor;
  if (first === second) return 0;

  const comparisonResult = first < second ? -1 : 1;
  return comparisonResult * directionFactor;
}

function compareStringValues(first: string, second: string, directionFactor: number): number {
  if (first === second) return 0;
  return (first < second ? -1 : 1) * directionFactor;
}

function compareNormalizedValues(
  first: NormalizedSortValue,
  second: NormalizedSortValue,
  direction: SortDirection
): number {
  const nullResult = compareNullValues(first, second);
  if (nullResult !== null) return nullResult;

  const directionFactor = direction === 'asc' ? 1 : -1;
  const numericResult = compareNumericValues(first.num, second.num, directionFactor);
  if (numericResult !== null) return numericResult;

  return compareStringValues(first.str, second.str, directionFactor);
}

function compareSortValues(
  first: any,
  second: any,
  sort: Sort,
  propPath?: string
): number {
  const firstValue = !propPath ? first[sort.id] : get(first, `${propPath}.${sort.id}`);
  const secondValue = !propPath ? second[sort.id] : get(second, `${propPath}.${sort.id}`);

  return compareNormalizedValues(
    normalizeSortValue(firstValue),
    normalizeSortValue(secondValue),
    sort.direction
  );
}

export function sortRbTableData(
  sort: Sort,
  data: any[],
  propPath?: string,
  compareFn?: (a: any, b: any, direction: SortDirection) => number
): any[] {
  const _data = data.slice();

  if (!sort.id || sort.direction === '') {
    return _data;
  }

  if (compareFn) {
    _data.sort((a, b) => compareFn(a, b, sort.direction));
  } else {
    _data.sort((a, b) => compareSortValues(a, b, sort, propPath));
  }
  return _data;
}